// partners/pages/PartnerLandingPage.jsx
// Complete React + Tailwind landing page for MNF Partner Program
// Uses MNF Figma design tokens (see F0_tailwind_config_extension.js)
// All colors strictly from Figma: #111111, #474747, #00C3FF, #741CFF, #051b1a

import { useState, useCallback, useEffect, useRef } from "react";
import { capturePartnerEvent } from "../../../utils/analytics";

function SectionLabel({ children }) {
  return (
    <div className="flex items-center gap-2 font-poppins text-xs font-semibold uppercase tracking-[3px] text-mnf-cyan mb-4 pointer-events-none cursor-default select-none">
      <span className="w-5 h-0.5 bg-mnf-gradient" />
      {children}
    </div>
  );
}

function CtaSpinner({ className = "border-mnf-btn-text/30 border-t-mnf-btn-text" }) {
  return (
    <span
      className={`w-4 h-4 border-2 rounded-full animate-spin shrink-0 ${className}`}
      aria-hidden="true"
    />
  );
}

function GradientBtn({ children, href, className = "", cta }) {
  const [isNavigating, setIsNavigating] = useState(false);

  const handleClick = (e) => {
    if (cta) capturePartnerEvent("partner_landing_cta_clicked", { cta });
    if (href?.startsWith("#")) {
      e.preventDefault();
      const el = document.getElementById(href.slice(1));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    setIsNavigating(true);
  };

  return (
    <a
      href={href}
      onClick={handleClick}
      aria-busy={isNavigating || undefined}
      className={`group inline-flex items-center justify-center gap-2.5 font-poppins font-semibold text-[15px] px-8 py-4 min-h-[48px] rounded-mnf-pill bg-mnf-gradient text-mnf-btn-text transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_32px_rgba(0,195,255,0.25)] cursor-pointer ${isNavigating ? "pointer-events-none opacity-70 cursor-wait" : ""} ${className}`}
    >
      {isNavigating ? (
        <>
          <CtaSpinner />
          {children}
        </>
      ) : (
        <>
          {children}
          <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14m-6-6l6 6-6 6" /></svg>
        </>
      )}
    </a>
  );
}

function OutlineBtn({ children, href, className = "", cta }) {
  const [isNavigating, setIsNavigating] = useState(false);

  const handleClick = (e) => {
    if (cta) capturePartnerEvent("partner_landing_cta_clicked", { cta });
    if (href?.startsWith("#")) {
      e.preventDefault();
      const el = document.getElementById(href.slice(1));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    setIsNavigating(true);
  };

  return (
    <a
      href={href}
      onClick={handleClick}
      aria-busy={isNavigating || undefined}
      className={`inline-flex items-center justify-center gap-2.5 font-poppins font-semibold text-[15px] px-8 py-4 min-h-[48px] rounded-mnf-pill border-[1.5px] border-white/10 text-mnf-text-primary transition-all duration-300 hover:border-mnf-cyan hover:text-mnf-cyan cursor-pointer ${isNavigating ? "pointer-events-none opacity-70 cursor-wait" : ""} ${className}`}
    >
      {isNavigating ? (
        <>
          <CtaSpinner className="border-mnf-cyan/30 border-t-mnf-cyan" />
          {children}
        </>
      ) : (
        children
      )}
    </a>
  );
}

function FaqItem({ question, answer, isOpen, onToggle }) {
  return (
    <div className="border-b border-white/[0.08]">
      <button onClick={onToggle} className="w-full flex justify-between items-center py-5 font-poppins text-base font-semibold text-left transition-colors hover:text-mnf-cyan">
        {question}
        <span className={`w-7 h-7 rounded-full bg-mnf-cyan/[0.08] border border-mnf-cyan/20 flex items-center justify-center text-lg text-mnf-cyan shrink-0 transition-all duration-300 ${isOpen ? "bg-mnf-cyan text-mnf-dark-1 rotate-45" : ""}`}>+</span>
      </button>
      <div className={`overflow-hidden transition-all duration-400 ${isOpen ? "max-h-96 pb-5" : "max-h-0"}`}>
        <p className="text-sm text-mnf-text-secondary leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

export default function PartnerLandingPage() {
  const [openFaq, setOpenFaq] = useState(0);
  const [calcRefs, setCalcRefs] = useState(15);
  const [calcType, setCalcType] = useState(30);
  const [calcPlan, setCalcPlan] = useState(49);
  const [interestDone, setInterestDone] = useState({});

  useEffect(() => {
    capturePartnerEvent("partner_landing_viewed");
  }, []);

  const calcTimerRef = useRef(null);
  const calcInitRef = useRef(true);
  useEffect(() => {
    if (calcInitRef.current) {
      calcInitRef.current = false;
      return undefined;
    }
    if (calcTimerRef.current) clearTimeout(calcTimerRef.current);
    calcTimerRef.current = setTimeout(() => {
      capturePartnerEvent("partner_calculator_changed", {
        referrals: calcRefs,
        commission_pct: calcType,
        plan_price: calcPlan,
      });
    }, 1000);
    return () => {
      if (calcTimerRef.current) clearTimeout(calcTimerRef.current);
    };
  }, [calcRefs, calcType, calcPlan]);

  const monthly = calcRefs * calcPlan * (calcType / 100);
  let yearTotal = 0;
  for (let m = 1; m <= 12; m++) yearTotal += calcRefs * calcPlan * (calcType / 100) * m;

  const handleInterest = useCallback((type) => {
    const email = prompt(`Enter your email to be notified when ${type.replace(/_/g, " ")} launches:`);
    if (email?.includes("@")) {
      fetch("/api/partners/interest/", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ track_type: type, email }) }).catch(() => {});
      setInterestDone((p) => ({ ...p, [type]: true }));
      capturePartnerEvent("partner_coming_soon_interest", { track_type: type });
    }
  }, []);

  const faqs = [
    { q: "How does referral tracking work?", a: "When someone clicks your referral link, a cookie tracks them (180 days for Ecosystem, 90 for Creative Ambassador). Purchases within that window are attributed to you. Track everything in your real-time dashboard." },
    { q: "When and how do I get paid?", a: "Monthly calculation, paid within 15 business days via UPI, bank transfer (NEFT/IMPS), or PayPal. Minimum: $1,000 (Ecosystem) / $500 (Creative Ambassador)." },
    { q: "Is there any cost to join?", a: "Zero. You also get a free MNF account — Studio for Ecosystem Partners, Pro for Creative Ambassadors — for the duration of your partnership." },
    { q: "What does \"lifetime commission\" mean?", a: "You earn commission on every payment your referral makes, for as long as they remain subscribed. No time cap." },
    { q: "Can I promote competing products?", a: "Yes — non-exclusive. We just ask you don't actively disparage MNF when promoting alternatives." },
    { q: "What's the approval criteria?", a: "Ecosystem: active film-industry business with verifiable clients. Creative Ambassador: 1,000+ followers in a relevant niche. We assess reach, relevance, content quality, and alignment." },
    { q: "How fast are applications reviewed?", a: "Within 48 hours. Onboarding in 5 business days with referral link, dashboard, and marketing kit." },
  ];

  const whyCards = [
    { icon: "🎯", title: "Built for Film, Not Generic", desc: "ChatGPT writes essays. MNF writes screenplays — with industry formatting, scene structure, and dialogue pacing." },
    { icon: "🔒", title: "Blockchain-Protected Scripts", desc: "Every script gets an immutable blockchain timestamp. No other AI tool protects IP this way." },
    { icon: "🌍", title: "15+ Indian Languages", desc: "Hindi, Tamil, Telugu, Malayalam, Bengali and more. MNF writes in the languages filmmakers need." },
    { icon: "📊", title: "Script → Pitch → Funding", desc: "No competitor takes a writer from blank page to investor pitch deck. MNF's Monetise tools are unique." },
    { icon: "💰", title: "Lifetime Recurring Commission", desc: "Most programs cap at 3-6 months. MNF pays you for as long as your referral subscribes." },
    { icon: "🤝", title: "True Partnership, Not Just Payouts", desc: "Co-branded pages, event sponsorship, content collaboration, beta access. We invest in partners." },
  ];

  const ecosystemBenefits = ["Co-branded landing page on mnf.ai", "Marketing kit in English & Hindi", "Priority support for your referrals", "Co-sponsored events (up to 2/year)", "180-day attribution cookie"];
  const ambassadorBenefits = ["Unique referral link + custom discount code", "Co-created content opportunities", "Cross-promotion on MNF channels", "Path to MNF Ambassador status", "90-day attribution cookie"];

  const comingSoon = [
    { icon: "🎓", title: "Film School Reseller", type: "school_reseller", desc: "Bundle MNF into curriculum with wholesale pricing.", rate: "Up to 50% margin" },
    { icon: "💼", title: "Strategic Power Partner", type: "power_partner", desc: "Use MNF tools to deliver consulting. 50/50 split.", rate: "50% revenue share" },
    { icon: "📺", title: "OTT Integration Partner", type: "ott_integration", desc: "Integrate MNF into content dev pipeline.", rate: "Custom enterprise deals" },
  ];

  const steps = [
    { num: "01", title: "Apply", desc: "3-minute form. Reviewed within 48 hours." },
    { num: "02", title: "Get Your Kit", desc: "Referral link, discount code, marketing assets, dashboard." },
    { num: "03", title: "Share", desc: "Recommend MNF through your natural channels." },
    { num: "04", title: "Earn", desc: "Monthly payouts via UPI/bank. Real-time tracking." },
  ];

  const testimonials = [
    { quote: "mnf.ai gave me confidence to experiment with story arcs. Context-aware scene suggestions let me explore without losing pacing.", name: "Meera Kapoor", role: "Short Film Writer", initials: "MK" },
    { quote: "Collaborating on mnf.ai unlocked new creative possibilities. Real-time collaboration refined every scene faster.", name: "Parikshit Khandelwal", role: "Solution Architect", initials: "PK" },
    { quote: "I feared AI would take over my storytelling, but mnf.ai proved to be a creative partner — keeping it mine.", name: "Ashima Jain", role: "Aspiring Filmmaker", initials: "AJ" },
  ];

  return (
    <div className="bg-mnf-dark-1 text-mnf-text-primary font-inter min-h-screen">
      {/* HERO */}
      <section className="relative px-[5%] pt-28 pb-24 overflow-hidden min-h-[88vh] flex items-center">
        <div className="absolute -top-[20%] -right-[10%] w-[700px] h-[700px] rounded-full bg-mnf-purple/[0.12] blur-3xl pointer-events-none" />
        <div className="absolute -bottom-[10%] -left-[5%] w-[600px] h-[600px] rounded-full bg-mnf-cyan/[0.08] blur-3xl pointer-events-none" />
        <div className="max-w-[1200px] mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
          <div className="animate-fade-up text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-mnf-cyan/[0.08] border border-mnf-cyan/20 px-4 py-1.5 rounded-mnf-pill font-poppins text-xs font-semibold text-mnf-cyan uppercase tracking-[1.5px] mb-6 pointer-events-none cursor-default">
              <span className="w-2 h-2 rounded-full bg-mnf-cyan animate-pulse-dot" />Partner Program Now Open
            </div>
            <h1 className="font-poppins font-extrabold text-4xl sm:text-5xl lg:text-[56px] leading-[1.08] mb-5">
              Earn While You<span className="block mt-1 bg-mnf-gradient bg-clip-text text-transparent">Empower Filmmakers</span>
            </h1>
            <p className="text-lg text-mnf-text-secondary leading-relaxed mb-8 max-w-[480px] mx-auto lg:mx-0">Join India's first AI filmmaking partner ecosystem. Earn up to 30% recurring commission — for life.</p>
            <div className="flex gap-3.5 flex-wrap justify-center lg:justify-start mb-10">
              <GradientBtn href="#types" cta="hero_explore">Explore Partnerships</GradientBtn>
              <OutlineBtn href="#how" cta="hero_how">How It Works</OutlineBtn>
            </div>
            <div className="flex gap-8 flex-wrap justify-center lg:justify-start">
              {[{ v: "30%", l: "Max Commission", c: "text-mnf-cyan" }, { v: "Lifetime", l: "Recurring Revenue", c: "text-purple-400" }, { v: "$0", l: "Joining Cost", c: "text-mnf-success" }, { v: "48hr", l: "Approval Time", c: "text-mnf-warning" }].map((s) => (
                <div key={s.l} className="cursor-default"><div className={`font-poppins text-[28px] font-extrabold leading-none ${s.c}`}>{s.v}</div><div className="text-xs text-mnf-text-muted uppercase tracking-wider mt-1">{s.l}</div></div>
              ))}
            </div>
          </div>
          <div className="relative animate-fade-up-delay hidden lg:block">
            <div className="grid grid-cols-2 grid-rows-2 gap-3 [perspective:800px] [transform:rotateY(-4deg)_rotateX(2deg)] hover:[transform:none] transition-transform duration-600">
              {[
                { src: "https://cdn.prod.website-files.com/6890527cc3ef5b2eae4f1b43/689b1b8b4153ebc500bcc23c_opt-asso-myron-05lgkS2eZRY-unsplash.avif", label: "Production Studios", sub: "Film Ecosystem Partners", span: "row-span-2" },
                { src: "https://cdn.prod.website-files.com/6890527cc3ef5b2eae4f1b43/69021875d4af47572eb84ad5_h-b-2.avif", label: "Screenwriters", sub: "Using AI Co-Write" },
                { src: "https://cdn.prod.website-files.com/6890527cc3ef5b2eae4f1b43/689b1bb17ed56883379edecb_opt-kyle-loftus-6oLabfCwBME-unsplash.avif", label: "Content Creators", sub: "Creative Ambassadors" },
              ].map((img) => (
                <div key={img.label} className={`rounded-mnf overflow-hidden border border-white/[0.08] relative ${img.span || ""}`}>
                  <img src={img.src} alt={img.label} className="w-full h-full object-cover brightness-[0.7] pointer-events-none" />
                  <div className="absolute inset-0 bg-gradient-to-t from-mnf-dark-1/80 to-transparent flex items-end p-4 pointer-events-none">
                    <div><div className="font-poppins text-[13px] font-semibold text-white">{img.label}</div><div className="text-[11px] text-mnf-text-secondary">{img.sub}</div></div>
                  </div>
                </div>
              ))}
            </div>
            <div className="absolute -top-3 -right-3 px-4 py-2.5 bg-mnf-dark-1/[0.88] backdrop-blur-xl border border-white/10 rounded-mnf font-poppins text-[13px] font-semibold z-10 animate-float-y pointer-events-none cursor-default">💰 Partners earning <span className="text-mnf-cyan">$15K+/mo</span></div>
            <div className="absolute bottom-5 -left-5 px-4 py-2.5 bg-mnf-dark-1/[0.88] backdrop-blur-xl border border-white/10 rounded-mnf font-poppins text-[13px] font-semibold z-10 animate-float-y [animation-delay:1s] pointer-events-none cursor-default">🎬 <span className="text-mnf-cyan">2K+</span> scripts on MNF</div>
          </div>
        </div>
      </section>

      {/* WHY MNF */}
      <section className="px-[5%] py-24 bg-mnf-dark-2 relative">
        <div className="absolute top-0 inset-x-0 h-px bg-mnf-gradient opacity-30" />
        <div className="max-w-[1200px] mx-auto">
          <h2 className="font-poppins text-3xl sm:text-4xl font-bold mb-4 leading-tight">Why Partners Choose MNF<br />Over Every Other AI Tool</h2>
          <p className="text-[17px] text-mnf-text-secondary max-w-[580px] leading-relaxed mb-12">Filmmakers are bombarded with AI tools. Here's why MNF converts.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyCards.map((c) => (
              <div key={c.title} className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf-lg p-8 cursor-default relative overflow-hidden">

                <div className="w-[52px] h-[52px] rounded-[14px] flex items-center justify-center text-2xl bg-mnf-cyan/[0.08] mb-4">{c.icon}</div>
                <h3 className="font-poppins text-lg font-semibold mb-2">{c.title}</h3>
                <p className="text-sm text-mnf-text-secondary leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNER TYPES */}
      <section className="px-[5%] py-24" id="types">
        <div className="max-w-[1200px] mx-auto">
          <SectionLabel>Partnership Tracks</SectionLabel>
          <h2 className="font-poppins text-3xl sm:text-4xl font-bold mb-4">Choose Your Track</h2>
          <p className="text-[17px] text-mnf-text-secondary max-w-[580px] mb-12">Two active tracks today, with more launching soon.</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-7">
            {/* Ecosystem */}
            <div className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf-xl overflow-hidden transition-all duration-400 hover:border-mnf-cyan/30 hover:-translate-y-1">
              <div className="p-8 pb-0 relative">
                <img src="https://cdn.prod.website-files.com/6890527cc3ef5b2eae4f1b43/690216361101892ec16909ee_e_h-4.avif" alt="Film Ecosystem" className="w-full h-[200px] object-cover rounded-mnf mb-5 brightness-75 pointer-events-none" />
                <span className="absolute top-11 left-11 font-poppins text-[11px] font-bold uppercase tracking-[1.5px] px-3.5 py-1.5 rounded-mnf-pill backdrop-blur-lg bg-mnf-purple/80 text-white">Featured</span>
              </div>
              <div className="px-8 pb-8">
                <h3 className="font-poppins text-2xl font-bold mb-1.5">Film Ecosystem Partner</h3>
                <p className="text-sm text-mnf-text-secondary mb-5">For production houses, casting agencies, equipment rentals, acting schools, editing studios, film festivals & collectives.</p>
                <div className="flex items-baseline gap-2.5 mb-6 p-4 bg-mnf-success/[0.04] border border-mnf-success/[0.12] rounded-mnf">
                  <span className="inline-flex items-baseline gap-1">
                    <span className="text-sm text-mnf-text-secondary font-semibold">upto</span>
                    <span className="font-poppins text-[44px] font-black text-mnf-success leading-none">30%</span>
                  </span>
                  <span className="text-sm text-mnf-text-secondary">Recurring commission<br />Lifetime of customer</span>
                </div>
                <ul className="space-y-2 mb-6">{ecosystemBenefits.map((b) => (<li key={b} className="text-sm text-mnf-text-secondary pl-7 relative before:content-['✓'] before:absolute before:left-0 before:text-mnf-cyan before:font-bold">{b}</li>))}</ul>
                <GradientBtn href="/partners/apply/ecosystem/" className="w-full justify-center" cta="apply_ecosystem">Apply as Ecosystem Partner</GradientBtn>
              </div>
            </div>
            {/* Ambassador */}
            <div className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf-xl overflow-hidden transition-all duration-400 hover:border-mnf-cyan/30 hover:-translate-y-1">
              <div className="p-8 pb-0 relative">
                <img src="https://cdn.prod.website-files.com/6890527cc3ef5b2eae4f1b43/6902160fca2408b3836eef7f_a2_pitch.avif" alt="Creative Ambassador" className="w-full h-[200px] object-cover rounded-mnf mb-5 brightness-75 pointer-events-none" />
                <span className="absolute top-11 left-11 font-poppins text-[11px] font-bold uppercase tracking-[1.5px] px-3.5 py-1.5 rounded-mnf-pill backdrop-blur-lg bg-mnf-cyan/80 text-mnf-btn-text">Most Popular</span>
              </div>
              <div className="px-8 pb-8">
                <h3 className="font-poppins text-2xl font-bold mb-1.5">Creative Ambassador</h3>
                <p className="text-sm text-mnf-text-secondary mb-5">For YouTubers, Instagram creators, bloggers, podcasters, and educators in filmmaking or screenwriting.</p>
                <div className="flex items-baseline gap-2.5 mb-6 p-4 bg-mnf-success/[0.04] border border-mnf-success/[0.12] rounded-mnf">
                  <span className="inline-flex items-baseline gap-1">
                    <span className="text-sm text-mnf-text-secondary font-semibold">upto</span>
                    <span className="font-poppins text-[44px] font-black text-mnf-success leading-none">25%</span>
                  </span>
                  <span className="text-sm text-mnf-text-secondary">Recurring commission<br />Lifetime of customer</span>
                </div>
                <ul className="space-y-2 mb-6">{ambassadorBenefits.map((b) => (<li key={b} className="text-sm text-mnf-text-secondary pl-7 relative before:content-['✓'] before:absolute before:left-0 before:text-mnf-cyan before:font-bold">{b}</li>))}</ul>
                <OutlineBtn href="/partners/apply/creative_ambassador/" className="w-full justify-center !border-mnf-cyan !text-mnf-cyan" cta="apply_ambassador">Apply as Creative Ambassador</OutlineBtn>
              </div>
            </div>
          </div>
          {/* Coming Soon */}
          <div className="mt-12">
            <SectionLabel>Expanding Soon</SectionLabel>
            <p className="text-[15px] text-mnf-text-secondary mb-5">Express interest — we'll notify you when these launch.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {comingSoon.map((c) => (
                <div key={c.type} className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf-lg p-7 transition-all hover:border-mnf-purple/30">
                  <div className="text-[28px] mb-3">{c.icon}</div>
                  <h4 className="font-poppins text-base font-semibold mb-1 flex items-center gap-2">{c.title}<span className="text-[10px] bg-mnf-purple/15 text-purple-400 px-2 py-0.5 rounded font-semibold">Soon</span></h4>
                  <p className="text-[13px] text-mnf-text-muted mb-3">{c.desc}</p>
                  <div className="text-[13px] text-mnf-success font-semibold mb-4">{c.rate}</div>
                  <button onClick={() => handleInterest(c.type)} disabled={interestDone[c.type]} className={`w-full py-2.5 rounded-lg font-poppins text-[13px] font-semibold transition-all ${interestDone[c.type] ? "bg-mnf-success/[0.08] border border-mnf-success/20 text-mnf-success" : "bg-mnf-purple/[0.08] border border-mnf-purple/20 text-purple-400 hover:bg-mnf-purple/15 cursor-pointer"}`}>{interestDone[c.type] ? "✓ We'll notify you!" : "Notify Me"}</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="px-[5%] py-24 bg-mnf-dark-2 border-y border-white/[0.08]" id="how">
        <div className="max-w-[1200px] mx-auto">
          <SectionLabel>Simple Process</SectionLabel>
          <h2 className="font-poppins text-3xl sm:text-4xl font-bold mb-12">How It Works</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => (
              <div key={s.num} className="text-center px-6 py-8 relative">
                {i < 3 && <div className="absolute top-[50px] right-0 w-px h-10 bg-white/10 hidden lg:block" />}
                <div className="font-poppins text-[56px] font-black bg-mnf-gradient bg-clip-text text-transparent opacity-20 leading-none mb-3">{s.num}</div>
                <h4 className="font-poppins text-base font-bold mb-2">{s.title}</h4>
                <p className="text-[13px] text-mnf-text-muted">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EARNINGS CALCULATOR */}
      <section className="px-[5%] py-24" id="calc">
        <div className="max-w-[1200px] mx-auto">
          <SectionLabel>Your Potential</SectionLabel>
          <h2 className="font-poppins text-3xl sm:text-4xl font-bold mb-10">Earnings Calculator</h2>
          <div className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf-xl p-12 relative overflow-hidden">
            <div className="absolute -top-1/2 -right-[20%] w-[400px] h-[400px] rounded-full bg-mnf-cyan/5 blur-3xl pointer-events-none" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10">
              <div className="space-y-6">
                <div><label className="block font-poppins text-xs font-semibold uppercase tracking-[1.5px] text-mnf-text-muted mb-2">Referrals per month</label><input type="range" min="1" max="100" value={calcRefs} onChange={(e) => setCalcRefs(+e.target.value)} className="w-full accent-[#00C3FF]" /><div className="flex justify-between text-xs text-mnf-text-muted mt-1"><span>1</span><span className="text-mnf-text-primary font-semibold">{calcRefs}</span><span>100</span></div></div>
                <div><label className="block font-poppins text-xs font-semibold uppercase tracking-[1.5px] text-mnf-text-muted mb-2">Partner Type</label><select value={calcType} onChange={(e) => setCalcType(+e.target.value)} className="w-full px-4 py-3.5 bg-mnf-dark-1 border border-white/[0.08] text-mnf-text-primary rounded-mnf font-inter focus:border-mnf-cyan focus:outline-none"><option value={30}>Film Ecosystem (30%)</option><option value={25}>Creative Ambassador (25%)</option></select></div>
                <div><label className="block font-poppins text-xs font-semibold uppercase tracking-[1.5px] text-mnf-text-muted mb-2">Average Plan</label><select value={calcPlan} onChange={(e) => setCalcPlan(+e.target.value)} className="w-full px-4 py-3.5 bg-mnf-dark-1 border border-white/[0.08] text-mnf-text-primary rounded-mnf font-inter focus:border-mnf-cyan focus:outline-none"><option value={59}>Creator (Monthly) $59/Month</option><option value={49}>Creator (Quarterly) $49/Month</option><option value={39}>Creator (Yearly) $39/Month</option></select></div>
              </div>
              <div className="text-center p-10 bg-mnf-dark-1 rounded-mnf-lg border border-white/[0.08]">
                <div className="text-[13px] text-mnf-text-muted uppercase tracking-wider mb-2">Monthly Approx. Earnings</div>
                <div className="font-poppins text-[52px] font-black bg-mnf-gradient bg-clip-text text-transparent leading-tight">${monthly.toLocaleString("en-US")}</div>
                <div className="text-sm text-mnf-text-secondary mt-1">recurring & compounding</div>
                <div className="text-[13px] text-mnf-text-muted mt-5 pt-5 border-t border-white/[0.08]">12-month total: <strong className="text-mnf-success">${yearTotal.toLocaleString("en-US")}</strong></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="px-[5%] py-24 bg-mnf-dark-2 border-t border-white/[0.08]">
        <div className="max-w-[1200px] mx-auto">
          <SectionLabel>Trust</SectionLabel>
          <h2 className="font-poppins text-3xl sm:text-4xl font-bold mb-10">What Creators Say About MNF</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf-lg p-7 cursor-default">
                <div className="text-mnf-warning text-sm tracking-widest mb-3">★★★★★</div>
                <blockquote className="text-sm text-mnf-text-secondary leading-relaxed mb-4 italic">&ldquo;{t.quote}&rdquo;</blockquote>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-mnf-gradient-135 flex items-center justify-center font-poppins font-bold text-white">{t.initials}</div>
                  <div><div className="font-poppins text-sm font-semibold">{t.name}</div><div className="text-xs text-mnf-text-muted">{t.role}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-[5%] py-24" id="faq">
        <div className="max-w-[1200px] mx-auto">
          <SectionLabel>Questions</SectionLabel>
          <h2 className="font-poppins text-3xl sm:text-4xl font-bold mb-10">FAQ</h2>
          <div className="max-w-[740px]">{faqs.map((f, i) => (<FaqItem key={i} question={f.q} answer={f.a} isOpen={openFaq === i} onToggle={() => { const next = openFaq === i ? -1 : i; setOpenFaq(next); capturePartnerEvent("partner_landing_faq_toggled", { question_index: i, is_open: next === i }); }} />))}</div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-[5%] py-24 text-center relative overflow-hidden" id="apply">
        <div className="absolute inset-0 bg-mnf-gradient-subtle pointer-events-none" />
        <div className="relative z-10">
          <h2 className="font-poppins text-3xl sm:text-4xl font-bold mb-4 max-w-[600px] mx-auto">Ready to Earn While You <span className="bg-mnf-gradient bg-clip-text text-transparent">Empower Filmmakers?</span></h2>
          <p className="text-[17px] text-mnf-text-secondary max-w-[500px] mx-auto mb-8">3 minutes to apply. Free to join. Start earning this month.</p>
          <div className="flex gap-3.5 justify-center flex-wrap">
            <GradientBtn href="/partners/apply/ecosystem/" cta="footer_ecosystem">Apply as Ecosystem Partner</GradientBtn>
            <OutlineBtn href="/partners/apply/creative_ambassador/" cta="footer_ambassador">Apply as Creative Ambassador</OutlineBtn>
          </div>
          <p className="text-xs text-mnf-text-muted mt-5">Applications reviewed within 48 hours · No commitment · Free to join</p>
        </div>
      </section>
    </div>
  );
}
